import { BaseInfoCompProps, useModePanelStyle, FontIcon, useModeFormStyle, capitalizeAll, BaseInfoCompActionsSet, BaseInfoCompSaveAction }
    from '@moderepo/mode-app-components';
import React, { CSSProperties, useCallback, useContext, useMemo, useState } from 'react';
import { Paper, Stepper, Step, StepLabel, StepContent, Button } from '@material-ui/core';
import clsx from 'clsx';
import produce, { castDraft, Draft } from 'immer';
import { CreateAlertRuleParams, UpdateAlertRuleParams, AlertConditionType, AlertRule, AlertTargetMetric, isMetricsAlertCondition, AlertAction,
    MetricsAlertCondition, MetricsHeartbeatAlertCondition, MetricsThresholdAlertCondition, AlertCondition, AlertMessageTemplate }
    from '@moderepo/mode-apis';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';
import { EditAlertRuleStepMetricsCondition, EditAlertRuleStepInfo, EditAlertRuleStepTargetMetric, useEditAlertRuleStyle,
    SubMetricsHeartbeatAlertCondition, SubMetricsThresholdAlertCondition, EditAlertRuleStepMessages, EditAlertRuleStepNotifications } from '.';
import { sortObjectAttributes } from '../../../utils/utils';



export enum EditAlertRuleStep {
    INFO = 'info',
    METRICS_CONDITION_TARGETS = 'metrics_condition_targets',
    METRICS_CONDITION_CONDITIONS = 'metrics_condition_conditions',
    MESSAGE_TEMPLATE = 'message_template',
    NOTIFICATIONS = 'notifications',
}


interface StepConfig {
    readonly name: EditAlertRuleStep;
    readonly label: string;
}


const stepsByAlertConditionType: {[alertConditionType: string]: StepConfig[]} = {
    // Steps required for HEARTBEAT alert rule type
    [AlertConditionType.METRIC_HEARTBEAT]: [{
        name : EditAlertRuleStep.INFO,
        label: 'alerting.edit_alert_rule.step_info.title',
    }, {
        name : EditAlertRuleStep.METRICS_CONDITION_TARGETS,
        label: 'alerting.edit_alert_rule.step_target_metrics.title',
    }, {
        name : EditAlertRuleStep.METRICS_CONDITION_CONDITIONS,
        label: 'alerting.edit_alert_rule.step_conditions.title',
    }, {
        name : EditAlertRuleStep.MESSAGE_TEMPLATE,
        label: 'alerting.edit_alert_rule.step_message_template.title',
    }, {
        name : EditAlertRuleStep.NOTIFICATIONS,
        label: 'alerting.edit_alert_rule.step_notifications.title',
    }],

    // Steps required for THRESHOLD alert rule type
    [AlertConditionType.METRIC_THRESHOLD]: [{
        name : EditAlertRuleStep.INFO,
        label: 'alerting.edit_alert_rule.step_info.title',
    }, {
        name : EditAlertRuleStep.METRICS_CONDITION_TARGETS,
        label: 'alerting.edit_alert_rule.step_target_metrics.title',
    }, {
        name : EditAlertRuleStep.METRICS_CONDITION_CONDITIONS,
        label: 'alerting.edit_alert_rule.step_conditions.title',
    }, {
        name : EditAlertRuleStep.MESSAGE_TEMPLATE,
        label: 'alerting.edit_alert_rule.step_message_template.title',
    }, {
        name : EditAlertRuleStep.NOTIFICATIONS,
        label: 'alerting.edit_alert_rule.step_notifications.title',
    }],
};



/**
 * The structure for the state where all the user inputs for all steps are stored.
 */
interface EditAlertRuleUserInputs {
    readonly stepInfo: {
        readonly name: string | undefined;
        readonly description: string | undefined;
    };
    readonly stepMetricsConditionTargets: {
        readonly target: AlertTargetMetric | undefined;
    };
    readonly stepMetricsConditionConditions: {
        readonly conditions: SubMetricsHeartbeatAlertCondition | SubMetricsThresholdAlertCondition | undefined
    };
    readonly stepMessages: {
        readonly title: string | undefined;
        readonly recoveryTitle: string | undefined;
    };
    readonly stepNotifications: {
        readonly actions: readonly AlertAction[] | undefined;
    }
}


/**
 * Convert an existing alert rule into an EditAlertRuleUserInputs object if there is one. If not, create a new EditAlertRuleUserInputs object.
 */
const convertAlertRuleObjectToInputs = (existingAlertRule: AlertRule | CreateAlertRuleParams | undefined): EditAlertRuleUserInputs => {
    if (existingAlertRule) {
        return {
            stepInfo: {
                name       : existingAlertRule.name,
                description: existingAlertRule.description,
            },
            stepMetricsConditionTargets: {
                target: isMetricsAlertCondition(existingAlertRule.condition) ? existingAlertRule.condition.metricsCondition : undefined,
            },
            stepMetricsConditionConditions: {
                conditions: isMetricsAlertCondition(existingAlertRule.condition) ? existingAlertRule.condition : undefined,
            },
            stepMessages: {
                title        : existingAlertRule.messageTemplate?.title,
                recoveryTitle: existingAlertRule.messageTemplate?.recoveryTitle,
            },
            stepNotifications: {
                actions: existingAlertRule.actions,
            },
        };
    }

    return {
        stepInfo: {
            name       : undefined,
            description: undefined,
        },
        stepMetricsConditionTargets: {
            target: undefined,
        },
        stepMetricsConditionConditions: {
            conditions: undefined,
        },
        stepMessages: {
            title        : undefined,
            recoveryTitle: undefined,
        },
        stepNotifications: {
            actions: undefined,
        },
    };
};



/**
 * This is the structure of the of the data we will be passing to the container when the user click "Save" to save the inputs.
 *
 * - If the component is being used for creating a new alert rule then the result object will contain an attribute "newAlertRule" which will be
 *   on object of CreateAlertRuleParams which has all the data needed to create a new alert rule.
 * - If the component is being used for updating an existing alert rule, the result object will contain an object call "updatedAlertRule".
 *   In this object, there will be an "alertRule" object of type UpdateAlertRuleParams which contains all the alert rule attributes that was
 *   updated. This "updatedAlertRule" will also contain the alertRuleId of the existing alert rule the user is editing
 *
 * alertRule object will be an object of type UpdateAlertRuleParams
 *   and the object will contain only attributes that have been updated, not all attributes. If no attribute have been updated, the object
 *   will be empty. Also, the 'alertRuleId' will be the ID of the alert rule being updated.
 */
export interface EditAlertRuleResult {
    readonly newAlertRule?: CreateAlertRuleParams | undefined;
    readonly updatedAlertRule?: {
        readonly alertRule: UpdateAlertRuleParams;
        readonly alertRuleId: number;
    } | undefined;
}


/**
 * Create the BASIC MetricsAlertCondition object based on the user inputs.
 */
const createMetricsAlertCondition = (
    conditionType: AlertConditionType.METRIC_HEARTBEAT | AlertConditionType.METRIC_THRESHOLD,
    inputs: EditAlertRuleUserInputs,
): MetricsAlertCondition => {
    const conditions = inputs.stepMetricsConditionConditions.conditions;
    const target = inputs.stepMetricsConditionTargets.target;

    if (!conditions || !conditions.delay || !conditions.frequency || !conditions.duration || !target) {
        throw new Error('error.message.unknown');
    }

    return {
        conditionType,
        delay           : conditions.delay,
        duration        : conditions.duration,
        frequency       : conditions.frequency,
        metricsCondition: target,
    };
};



/**
 * Create specifically MetricsHeartbeatAlertCondition object based on the user inputs.
 */
const createMetricsHeartbeatAlertCondition = (
    conditionType: AlertConditionType.METRIC_HEARTBEAT,
    inputs: EditAlertRuleUserInputs,
): MetricsHeartbeatAlertCondition => {
    // First call the "createMetricsAlertCondition" to create the BASIC attributes for MetricsAlertCondition
    const result = createMetricsAlertCondition(conditionType, inputs);

    // Then add addition attributes for METRIC HEARTBEAT condition
    const conditions = inputs.stepMetricsConditionConditions.conditions as SubMetricsHeartbeatAlertCondition;
    if (!conditions || !conditions.severity) {
        throw new Error('error.message.unknown');
    }

    return {
        ...result,
        conditionType: AlertConditionType.METRIC_HEARTBEAT,
        severity     : conditions.severity,
    };
};



/**
 * Create specifically MetricsThresholdAlertCondition object based on the user inputs.
 */
const createMetricsThresholdAlertCondition = (
    conditionType: AlertConditionType.METRIC_THRESHOLD,
    inputs: EditAlertRuleUserInputs,
): MetricsThresholdAlertCondition => {
    // First call the "createMetricsAlertCondition" to create the BASIC attributes for MetricsAlertCondition
    const result = createMetricsAlertCondition(conditionType, inputs);

    // Then add addition attributes for METRIC HEARTBEAT condition
    const conditions = inputs.stepMetricsConditionConditions.conditions as SubMetricsThresholdAlertCondition;
    if (!conditions || !conditions.aggregation || !conditions.thresholds || conditions.thresholds.length === 0) {
        throw new Error('error.message.unknown');
    }

    return {
        ...result,
        conditionType: AlertConditionType.METRIC_THRESHOLD,
        aggregation  : conditions.aggregation,
        thresholds   : conditions.thresholds,
    };
};



/**
 * Create the "condition" object based on the given user inputs. This function will call other helper function based on the
 * condition type. This function will throw errors if the conditionType is not supported.
 */
const createAlertCondition = (conditionType: AlertConditionType, inputs: EditAlertRuleUserInputs): AlertCondition => {
    if (conditionType === AlertConditionType.METRIC_HEARTBEAT) {
        return createMetricsHeartbeatAlertCondition(conditionType, inputs);
    }

    if (conditionType === AlertConditionType.METRIC_THRESHOLD) {
        return createMetricsThresholdAlertCondition(conditionType, inputs);
    }

    // We don't support other condition type so it shouldn't get to here
    throw new Error('error.message.unknown');
};


/**
 * Create an CreateAlertRuleParams object based on the user input. This will return an object containing all the attributes/values
 * required for creating a new alert rule
 */
const createNewAlertRuleObject = (
    conditionType: AlertConditionType,
    inputs: EditAlertRuleUserInputs,
): CreateAlertRuleParams => {
    // Inputs should already been validated before calling this function therefore the values shouldn't be undefined. However, if for
    // some reason they are, throw an error.
    if (!inputs.stepInfo.name || !inputs.stepInfo.description || !inputs.stepMessages.title || !inputs.stepNotifications.actions) {
        throw new Error('error.message.unknown');
    }
    return {
        enabled        : true,
        name           : inputs.stepInfo.name,
        description    : inputs.stepInfo.description,
        actions        : inputs.stepNotifications.actions,
        condition      : createAlertCondition(conditionType, inputs),
        messageTemplate: {
            title        : inputs.stepMessages.title,
            recoveryTitle: inputs.stepMessages.recoveryTitle,
        },
    };
};


/**
 * Create an UpdateAlertRule object based on the existing alert rule object and the user inputs.
 * This will compare the attributes values between the existing alert rule and new attributes values from the user inputs and return
 * an object containing only the attributes/values that have been modified.
 */
const createUpdatedAlertRuleObject = (
    existingAlertRule: AlertRule,
    inputs: EditAlertRuleUserInputs,
): UpdateAlertRuleParams => {

    // Create an alert rule condition based on the new inputs. We will use this condition if this condition is different from the
    // existing alert rule's condition.
    const newAlertRuleCondition = createAlertCondition(existingAlertRule.condition.conditionType, inputs);

    const newMessageTemplate: AlertMessageTemplate = {
        title        : inputs.stepMessages.title ?? existingAlertRule.messageTemplate?.title,
        recoveryTitle: inputs.stepMessages.recoveryTitle ?? existingAlertRule.messageTemplate?.recoveryTitle,
    };

    const newActions = inputs.stepNotifications.actions;

    const updatedAlertRule: UpdateAlertRuleParams = {
        // Check if existing alert rule's name or description is different from the name/description in new inputs.
        // If they are then add the new name/description to the result.
        name       : inputs.stepInfo.name && inputs.stepInfo.name !== existingAlertRule.name ? inputs.stepInfo.name : undefined,
        description: inputs.stepInfo.description && inputs.stepInfo.description !== existingAlertRule.description
            ? inputs.stepInfo.description : undefined,

        // For condition, there are too many attributes to compare so we will just compare the string representation of the existing alert
        // rule's condition VS the condition object based on the new inputs. If their strings are different, we will assume the one of
        // the condition's attributes are different therefore we will update the entire condition object. However, 2 objects can have exactly
        // the same attributes and values but have different string representation because the attributes are in different order. Therefore,
        // before we JSON.stringify them, we will recursively sort their attributes first.
        condition: JSON.stringify(sortObjectAttributes(existingAlertRule.condition)) !== JSON.stringify(sortObjectAttributes(newAlertRuleCondition))
            ? newAlertRuleCondition : undefined,

        // If one of the messages in the newMessageTemplate is different from the existingAlertRule.messageTemplate then replace
        // existingAlertRule.messageTemplate with the newMessageTemplate. We will JSON.stringify them and compare their string presentation
        // to determine if they are different
        messageTemplate: JSON.stringify(sortObjectAttributes(existingAlertRule.messageTemplate))
            !== JSON.stringify(sortObjectAttributes(newMessageTemplate)) ? newMessageTemplate : undefined,

        actions: newActions
            && JSON.stringify(sortObjectAttributes(existingAlertRule.actions)) !== JSON.stringify(sortObjectAttributes(newActions))
            ? newActions : undefined,
    };

    return updatedAlertRule;
};



/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action. The "save" action onClick handler will be provided with an object of type AlertRuleEditorResult
 */
export interface AlertRuleEditorActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<EditAlertRuleResult>
}


/**
 * A function to check if a provided is an AlertRule object or just a clone. A clone will NOT have an ID.
 */
const isAlertRule = (obj: AlertRule | CreateAlertRuleParams): obj is AlertRule => {
    const alertRule = obj as AlertRule;
    return alertRule !== undefined && alertRule.id !== undefined;
};


/**
 * Props required for EditAlertRuleInfoComp
 */
export interface EditAlertRuleInfoCompProps extends Omit<BaseInfoCompProps, 'fields'> {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // targetAlertRule is either an AlertRule object (for Edit Alert Rule mode), or an AlertConditionType value (for CREATE Alert Rule mode)
    readonly targetAlertRule: AlertRule | AlertConditionType | CreateAlertRuleParams;

    // To automatically jump to the specified step on comp init
    readonly jumpToStep?: EditAlertRuleStep | undefined;

    // Override the BaseInfoCompProps's actions props
    readonly actions?: AlertRuleEditorActionsSet | undefined;
}



/**
 * This is the Alert Rule editor dumb component. However, it is not really 100% dumb component like other components. It will have some logics
 * to validate the inputs.
 */
export const EditAlertRuleInfoComp: React.FC<EditAlertRuleInfoCompProps> = (props: EditAlertRuleInfoCompProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useEditAlertRuleStyle();
    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();

    // Get the alertRule object from the props.targetAlertRule if it is an Object.
    const existingAlertRule = typeof props.targetAlertRule === 'object' && isAlertRule(props.targetAlertRule) ? props.targetAlertRule : undefined;
    const existingAlertRuleClone = typeof props.targetAlertRule === 'object' && !isAlertRule(props.targetAlertRule)
        ? props.targetAlertRule : undefined;

    // If props.targetAlertRule is a alert rule condition type string then use it as the newAlertRuleType. Else, use the existingAlertRuleClone's
    // conditionType if it exist.
    const newAlertRuleType = typeof props.targetAlertRule === 'string' ? props.targetAlertRule : existingAlertRuleClone?.condition.conditionType;

    // The alertRuleType either from existing alertRule object or from newAlertRuleType or from newAlertRuleClone.
    const alertRuleType = (existingAlertRule?.condition.conditionType ?? newAlertRuleType) as AlertConditionType;

    // The states where all the user input values are saved after each step is completed
    const [editAlertRuleInputs, setEditAlertRuleInputs] = useState<EditAlertRuleUserInputs>(convertAlertRuleObjectToInputs(
        existingAlertRule ?? existingAlertRuleClone,
    ));

    // Get the alert rule set up steps the user need to go through based on the existing alert rule's condition type or the newAlertRuleType
    // of newAlertRuleClone's condition type
    const steps = stepsByAlertConditionType[(existingAlertRule?.condition.conditionType ?? newAlertRuleType) as string];

    // The state to keep track of the current step. Initialize it with the props.jumpToStep value.
    const [currentStep, setCurrentStep] = useState<number>(props.jumpToStep ? steps.findIndex((s: StepConfig) => {
        return s.name === props.jumpToStep;
    }) : 0);

    // To keep track of the errors for each step so we know which step has error
    const [errorsByStepName, setErrorsByStepName] = useState<{[step: string]: boolean}>({});


    /**
     * On Step click, we can take the user directly to that step BUT we will only allow the user to go to a specific step when the user finished
     * all the steps
     */
    const onStepClick = useCallback((step: number) => {
        if (currentStep >= steps.length) {
            setCurrentStep(step);
        }
    }, [currentStep, steps.length]);


    /**
     * On Step Info completed, save the inputs.
     */
    const onStepInfoCompleted = useCallback((alertRuleName: string, alertRuleDescription: string) => {
        setEditAlertRuleInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<EditAlertRuleUserInputs>) => {
                draft.stepInfo.name = alertRuleName;
                draft.stepInfo.description = alertRuleDescription;
            });
        });
    }, []);



    /**
     * On Step Target Metric completed, save the inputs.
     */
    const onStepTargetMetricCompleted = useCallback((targetMetric: AlertTargetMetric) => {
        setEditAlertRuleInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<EditAlertRuleUserInputs>) => {
                draft.stepMetricsConditionTargets.target = castDraft(targetMetric);
            });
        });
    }, []);


    /**
     * On Step Target Metrics completed, save the inputs.
     */
    const onStepMetricsConditionsCompleted = useCallback((conditions: SubMetricsHeartbeatAlertCondition | SubMetricsThresholdAlertCondition) => {
        setEditAlertRuleInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<EditAlertRuleUserInputs>) => {
                draft.stepMetricsConditionConditions.conditions = castDraft(conditions);
            });
        });
    }, []);



    /**
     * On Step Messages completed, save the inputs.
     */
    const onStepMessagesCompleted = useCallback((title: string, recoveryTitle?: string | undefined) => {
        setEditAlertRuleInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<EditAlertRuleUserInputs>) => {
                draft.stepMessages.title = title;
                draft.stepMessages.recoveryTitle = recoveryTitle;
            });
        });
    }, []);


    const onStepNotificationsCompleted = useCallback((actions: readonly AlertAction[]) => {
        setEditAlertRuleInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<EditAlertRuleUserInputs>) => {
                draft.stepNotifications.actions = castDraft(actions);
            });
        });
    }, []);



    /**
     * On Prev step button clicked. Take the user to the previous step but make sure the current step does not have any error
     */
    const onPrevStep = useCallback(() => {
        setCurrentStep((prevStep: number) => {
            return prevStep - 1;
        });
    }, []);


    /**
     * On Next step button clicked. Take the user to the next step but make sure the current step does not have any error
     */
    const onNextStep = useCallback(() => {
        const stepConfig = steps[currentStep];
        if (!stepConfig || !errorsByStepName[stepConfig.name]) {
            setCurrentStep((prevStep: number) => {
                return prevStep + 1;
            });
        }
    }, [steps, currentStep, errorsByStepName]);



    /**
     * On go to the end clicked. Need to make sure none of the step contain errors
     */
    const onGotoLastStep = useCallback(() => {
        const hasErrors = Object.values(errorsByStepName).some((error) => {
            return error === true;
        });
        if (!hasErrors) {
            setCurrentStep(steps.length);
        }
    }, [steps.length, errorsByStepName]);



    /**
     * On submit button clicked, call the onFormSubmit handler and pass it the user input values
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            try {
                if (newAlertRuleType) {
                    props.actions.saveUpdate.onClick({
                        newAlertRule: createNewAlertRuleObject(alertRuleType, editAlertRuleInputs),
                    });
                } else if (existingAlertRule) {
                    props.actions.saveUpdate.onClick({
                        updatedAlertRule: {
                            alertRule  : createUpdatedAlertRuleObject(existingAlertRule, editAlertRuleInputs),
                            alertRuleId: existingAlertRule.id,
                        },
                    });
                }
            } catch (error) {
                uiDispatch(showErrorNotification((error as Error).message));
            }
        }

        return false;
    }, [props.actions?.saveUpdate, newAlertRuleType, existingAlertRule, alertRuleType, editAlertRuleInputs, uiDispatch]);


    // Check if we have all the inputs and can save form. This will be used for enable/disable the "Save" button
    const canSaveForm = useMemo((): boolean => {
        return currentStep >= steps.length                                                                  // We passed the last step
            && Object.values(errorsByStepName).every((hasError: boolean): boolean => {                      // None of the step has error
                return !hasError;
            })
            && editAlertRuleInputs.stepInfo !== undefined                                                   // We have the Step Info inputs
            && editAlertRuleInputs.stepMetricsConditionTargets !== undefined                                // We have the Step Metrics Target inputs
            && editAlertRuleInputs.stepMetricsConditionConditions !== undefined                             // We have the Step Condition inputs
            && editAlertRuleInputs.stepMessages !== undefined;                                              // We have the Step messages inputs
    }, [currentStep, editAlertRuleInputs.stepInfo, editAlertRuleInputs.stepMessages, editAlertRuleInputs.stepMetricsConditionConditions,
        editAlertRuleInputs.stepMetricsConditionTargets, errorsByStepName, steps.length]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <div className={clsx(panelClasses.panelHeader)}>
                <div className={clsx(panelClasses.panelHeaderIconContainer)}>
                    {props.icon && <FontIcon className={clsx(panelClasses.panelHeaderIcon)} iconName={props.icon} />}
                </div>
                <div className={panelClasses.panelHeaderTitle}>
                    {props.title}
                </div>
            </div>
            <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                <form
                    className={
                        clsx(formClasses.root, props.formProps?.className)
                    }
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <Stepper
                        className={classes.stepper}
                        activeStep={currentStep}
                        orientation="vertical"
                    >
                        {steps.map((stepConfig: StepConfig, index: number) => {
                            return (
                                <Step className={classes.step} key={stepConfig.label}>
                                    <StepLabel
                                        className={classes.stepLabel}
                                        error={Boolean(errorsByStepName[stepConfig.name])}
                                        optional={errorsByStepName[stepConfig.name] ? trans('alerting.edit_alert_rule.generic_error_message') : ''}
                                        onClick={() => {
                                            onStepClick(index);
                                        }}
                                    >
                                        {capitalizeAll(trans(stepConfig.label))}
                                    </StepLabel>
                                    <StepContent className={classes.stepContent}>
                                        {stepConfig.name === EditAlertRuleStep.INFO && (
                                            <EditAlertRuleStepInfo
                                                projectId={props.projectId}
                                                alertConditionType={alertRuleType}
                                                isNewAlertRule={!existingAlertRule}
                                                alertRuleName={editAlertRuleInputs.stepInfo.name}
                                                alertRuleDescription={editAlertRuleInputs.stepInfo.description}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === steps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepInfoCompleted}
                                            />
                                        )}

                                        {stepConfig.name === EditAlertRuleStep.METRICS_CONDITION_TARGETS && (
                                            <EditAlertRuleStepTargetMetric
                                                projectId={props.projectId}
                                                alertConditionType={alertRuleType}
                                                isNewAlertRule={!existingAlertRule}
                                                alertRuleTargetMetric={editAlertRuleInputs.stepMetricsConditionTargets.target}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === steps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepTargetMetricCompleted}
                                            />
                                        )}

                                        {stepConfig.name === EditAlertRuleStep.METRICS_CONDITION_CONDITIONS && (
                                            <EditAlertRuleStepMetricsCondition
                                                projectId={props.projectId}
                                                alertConditionType={alertRuleType}
                                                isNewAlertRule={!existingAlertRule}
                                                alertRuleTargetMetric={editAlertRuleInputs.stepMetricsConditionTargets.target}
                                                alertRuleCondition={editAlertRuleInputs.stepMetricsConditionConditions.conditions}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === steps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepMetricsConditionsCompleted}
                                            />
                                        )}

                                        {stepConfig.name === EditAlertRuleStep.MESSAGE_TEMPLATE && (
                                            <EditAlertRuleStepMessages
                                                projectId={props.projectId}
                                                alertConditionType={alertRuleType}
                                                isNewAlertRule={!existingAlertRule}
                                                title={editAlertRuleInputs.stepMessages.title}
                                                recoveryTitle={editAlertRuleInputs.stepMessages.recoveryTitle}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === steps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepMessagesCompleted}
                                            />
                                        )}

                                        {stepConfig.name === EditAlertRuleStep.NOTIFICATIONS && (
                                            <EditAlertRuleStepNotifications
                                                projectId={props.projectId}
                                                alertConditionType={alertRuleType}
                                                isNewAlertRule={!existingAlertRule}
                                                alertRuleActions={editAlertRuleInputs.stepNotifications.actions}
                                                canGoBack={index > 0}
                                                isLastStep={currentStep === steps.length - 1}
                                                onPrevStepClick={onPrevStep}
                                                onNextStepClick={onNextStep}
                                                onGotoLastStepClick={onGotoLastStep}
                                                onStepError={(hasErrors: boolean) => {
                                                    setErrorsByStepName(produce(errorsByStepName, (draft: typeof errorsByStepName) => {
                                                        draft[stepConfig.name] = hasErrors;
                                                    }));
                                                }}
                                                onStepCompleted={onStepNotificationsCompleted}
                                            />
                                        )}
                                    </StepContent>
                                </Step>
                            );
                        })}
                    </Stepper>

                    {props.isEditing && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div className={classes.allStepsCompletedMessage}>
                                {currentStep >= steps.length && (
                                    trans('alerting.edit_alert_rule.all_steps_completed_message')
                                )}
                            </div>
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        disabled={!canSaveForm}
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                </form>
            </div>
        </Paper>
    );
};
